import { z } from "zod";
import { zStringDate, zStringId } from "../../utils";
import { dbRef } from "../../db-ref";

export const zUserSnippet = z.object({
  _id: zStringId(),
  username: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  country: z.string(),
  profileImage: z.string(),
  createdAt: zStringDate(),
});

/**
 * Common User fields present on other resources
 */
export type UserSnippet = z.infer<typeof zUserSnippet>;

/**
 * This pipeline generates the main and project stages of fetching a User Snippet resource.
 *
 * Main stages just looks up the user given the localField as id.
 * The Project stage ensure that the last name is hidden as per the user preference
 * @param localField Name of the field which contains the id of the User
 * @param as Name of the field where the User doc should be set
 * @returns
 */
export function getUserSnippetPipeline(localField: string, as: string) {
  const projectFields = generateUserSnippetFields("", `$${as}`);

  return {
    _z: zUserSnippet,
    mainStages: [
      {
        $lookup: {
          from: dbRef.user.collection,
          localField,
          foreignField: "_id",
          as,
        },
      },
      {
        $unwind: {
          path: `$${as}`,
          preserveNullAndEmptyArrays: true,
        },
      },
    ],
    // only project the fields if the source (localField) exists
    projectStage: {
      [`${as}`]: {
        $cond: {
          if: { $ne: [{ $type: `$${localField}` }, "missing"] },
          then: {
            ...projectFields,
          },
          else: "$$REMOVE",
        },
      },
    },
  };
}

/**
 * This pipeline generates a map aggregation function to be used in the project stage of a resource that has user docs in an array.
 * It expects full user docs in the array. The lookup, grouping etc to populate an id array with user docs must be done before hand.
 *
 * The function just takes the user docs in the array and maps them to the User Snippet fields, ensuring that the last name is hidden as per the user preference.
 * @param arrayField Name of field that contains the array of User docs
 */
export function getArrayOfUserSnippetsProjection(arrayField: string) {
  const mapLocalName = "doc";
  const mapFields = generateUserSnippetFields("", `$$${mapLocalName}`);

  return {
    $map: {
      input: `$${arrayField}`,
      as: mapLocalName,
      in: { ...mapFields },
    },
  };
}

/**
 * Helper function to generate the fields for User Snippet
 * @param fieldNamePrefix Prefix to attach to field name
 * @param refName User doc variable to lookup the fields from
 * @returns
 */
function generateUserSnippetFields(fieldNamePrefix: string, refName: string) {
  return {
    [`${fieldNamePrefix}_id`]: `${refName}._id`,
    [`${fieldNamePrefix}username`]: `${refName}.username`,
    [`${fieldNamePrefix}firstName`]: `${refName}.firstName`,
    ...generateUserResourceLastNameField(fieldNamePrefix, refName),
    [`${fieldNamePrefix}country`]: `${refName}.country`,
    [`${fieldNamePrefix}profileImage`]: `${refName}.profileImage`,
    ...generateUserResourceCreatedAtField(fieldNamePrefix, refName),
  };
}

function generateUserResourceLastNameField(fieldNamePrefix?: string, refName?: string) {
  return {
    [`${fieldNamePrefix}lastName`]: {
      $cond: {
        if: {
          $eq: [`${refName}.nameVisibility`, 1],
        },
        then: {
          $concat: [{ $substr: [`${refName}.lastName`, 0, 1] }, "."],
        },
        else: `${refName}.lastName`,
      },
    },
  };
}

function generateUserResourceCreatedAtField(fieldNamePrefix?: string, refName?: string) {
  return {
    [`${fieldNamePrefix}createdAt`]: {
      $ifNull: [`${refName}.createdAt`, `${refName}.created`],
    },
  };
}
